// src/components/flipbooks/FlipbookSettings.jsx
import React, { useState, useEffect } from 'react';
import { BookOpen, Settings, Palette, Globe, Search, Shield, Layout, Upload, Trash2, ImageIcon,
  Maximize2, Monitor, Users, GraduationCap, Award, MessageSquarePlus } from 'lucide-react';
import { SettingsModal } from '../settings/shared/SettingsModal';
import { TabNavigation } from '../settings/shared/TabNavigation';
import { BasicInfoTab } from '../settings/shared/BasicInfoTab';
import { DesignBrandingSection } from '../settings/shared/DesignSection';
import { HeaderSettings } from '../settings/shared/HeaderSettings';
import { PrivacySection } from '../settings/shared/PrivacySection';
import { SEOSection } from '../settings/shared/SEOSection';
import { firestoreService } from '../../services/firestoreService';

export const FlipbookSettings = ({ book, bookId, onUpdate, onClose }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadStates, setUploadStates] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [logoFile, setLogoFile] = useState(null);
    const [coverImageFile, setCoverImageFile] = useState(null);
    const [headerBackgroundFile, setHeaderBackgroundFile] = useState(null);
    const [faviconFile, setFaviconFile] = useState(null); // ADD THIS LINE
    const [ogImageFile, setOgImageFile] = useState(null); 
    
    // Preview states
    const [logoPreview, setLogoPreview] = useState('');
    const [coverImagePreview, setCoverImagePreview] = useState('');
    const [headerBackgroundPreview, setHeaderBackgroundPreview] = useState('');
    const [faviconPreview, setFaviconPreview] = useState(''); // ADD THIS LINE
    const [ogImagePreview, setOgImagePreview] = useState('');

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: BookOpen },
    { id: 'design', label: 'Design & Styling', icon: Palette },
    { id: 'header', label: 'Header', icon: Layout },
    { id: 'media', label: 'Media Settings', icon: ImageIcon },
    { id: 'privacy', label: 'Privacy & Access', icon: Shield },
    { id: 'flipbook', label: 'Flipbook Settings', icon: Settings },
    { id: 'seo', label: 'SEO & Metadata', icon: Search }
  ];

  useEffect(() => {
    initializeFormData();
  }, [book]);

  const initializeFormData = () => {
    setFormData({
        botName: book?.botName || '',
        description: book?.description || '',
        botCategory: book?.botCategory || '',
        logoUrl: book?.logoUrl || '',
        coverImageUrl: book?.coverImageUrl || '',

        published: book?.published !== false,
        schedulePublication: book?.schedulePublication || false,
        publicationDate: book?.publicationDate || '',
        publicationTime: book?.publicationTime || '',
        
        // Access & Privacy Settings
        accessLevel: book?.accessLevel || 'public',
        requireLogin: book?.requireLogin || false,
        allowIndexing: book?.allowIndexing !== false,
        authorizedUsers: book?.authorizedUsers || [],
        allowPrint: book?.allowPrint !== false,
        allowDownload: book?.allowDownload || false,
        enableRightClickProtection: book?.enableRightClickProtection || false,
        showLastUpdate: book?.showLastUpdate !== false,
        requireAge: book?.requireAge || false,
        ageLimit: book?.ageLimit || 18,
        requireTerms: book?.requireTerms || false,
        passwordProtected: book?.passwordProtected || false,
        accessPassword: book?.accessPassword || '',
    
        primaryColor: book.styling?.primaryColor || '#0795ff',
        backgroundColor: book.styling?.backgroundColor || '#ffffff',
        headerColor: book.styling?.headerColor || '#ffffff',
        headerTextColor: book.styling?.headerTextColor || '#333333',
        headerOpacity: book.styling?.headerOpacity || '100',
        textColor: book?.styling?.textColor || '#1f2937',
        accentColor: book.styling?.accentColor || '#f59e0b',
        fontFamily: book.styling?.fontFamily || 'Inter, sans-serif',
        headerRadius: book.styling?.headerRadius || '0px',
        cardRadius: book.styling?.cardRadius || '12px',
        shadowLevel: book.styling?.shadowLevel || 'medium',
        logoInvert: book.styling?.logoInvert || 0,
        gallerySidebarBg: book.styling?.gallerySidebarBg || '#f9fafb',
        gallerySidebarShadow: book.styling?.gallerySidebarShadow || '#000000',
      
      headerSettings: {
          // Element Visibility
          showLogo: book.headerSettings?.showLogo ?? true,
          showTitle: book.headerSettings?.showTitle ?? true,
          showDescription: book.headerSettings?.showDescription ?? false,
          showSearch: book.headerSettings?.showSearch ?? true,
          showShareButton: book.headerSettings?.showShareButton ?? true,
          showAccessLevel: book.headerSettings?.showAccessLevel ?? true,
          showFilterControls: book.headerSettings?.showFilterControls ?? true,
          showGallerySelector: book.headerSettings?.showGallerySelector ?? true,
          showViewModeToggle: book.headerSettings?.showViewModeToggle ?? true,
          showOwnerControls: book.headerSettings?.showOwnerControls ?? true,

          // Styling
          headerHeight: book.headerSettings?.headerHeight || 80,
          logoHeight: book.headerSettings?.logoHeight || 40,
          position: book.headerSettings?.position || 'sticky',
          layout: book.headerSettings?.layout || 'default',

          // Search Configuration
          searchPlaceholder: book.headerSettings?.searchPlaceholder || 'Search media...',
          searchPosition: book.headerSettings?.searchPosition || 'center',
          enableLiveSearch: book.headerSettings?.enableLiveSearch ?? true,
          showSearchOnMobile: book.headerSettings?.showSearchOnMobile ?? false,

          // User Profile
          userProfileVisibility: book.headerSettings?.userProfileVisibility || 'auto',
          showProfileForPublic: book.headerSettings?.showProfileForPublic ?? false,
          requireLoginToView: book.headerSettings?.requireLoginToView ?? false,

          ...book.headerSettings
        },

      flipbookSettings: {
        viewMode: book?.flipbookSettings?.viewMode || 'double',
        enableSound: book?.flipbookSettings?.enableSound ?? true,
        enableNarration: book?.flipbookSettings?.enableNarration ?? true,
        autoPlay: book?.flipbookSettings?.autoPlay ?? false,
        showPageNumbers: book?.flipbookSettings?.showPageNumbers ?? true,
        flipDuration: book?.flipbookSettings?.flipDuration || 1000,
        shadowOpacity: book?.flipbookSettings?.shadowOpacity || 0.5,
        pageSize: book?.flipbookSettings?.pageSize || '',
        orientation: book?.flipbookSettings?.orientation || '',
        minWidth: book?.flipbookSettings?.minWidth || 300,
        maxWidth: book?.flipbookSettings?.maxWidth || 1000,
        minHeight: book?.flipbookSettings?.minHeight || 400,
        maxHeight: book?.flipbookSettings?.maxHeight || 1500,
        allowedResponseTypes: book?.flipbookSettings?.allowedResponseTypes || ['practice', 'graded', 'model'],
        editors: book?.flipbookSettings?.editors || [],
        enableResponseModal: book?.flipbookSettings?.enableResponseModal ?? true,
        enableGradingSidebar: book?.flipbookSettings?.enableGradingSidebar ?? false,
      },
      
      // Media Settings - ADD THESE
      maxMediaSize: book?.maxMediaSize || 10,
      autoGenerateThumbnails: book?.autoGenerateThumbnails ?? true,
      enableImageOptimization: book?.enableImageOptimization ?? true,
      
      seoTitle: book?.seoTitle || book?.botName || '',
      seoDescription: book?.seoDescription || book?.description || '',
      seoKeywords: book?.seoKeywords || [],
      ogImage: book?.ogImage || book?.coverImageUrl || '',
      customDomain: book?.customDomain || '',
      customPath: book?.customPath || '',
      customCSS: book?.customCSS || '',
      favicon: book?.favicon || '',
      logoUrl: book?.logoUrl || '',
      headerBackgroundUrl: book.headerBackgroundUrl || '',
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = field.split('.');
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
    setHasChanges(true);
  };

  const PAGE_SIZES = {
    DL: {
      portrait: { width: 374, height: 794 },
      landscape: { width: 794, height: 374 }
    },
    A5: { 
      portrait: { width: 560, height: 794 },
      landscape: { width: 794, height: 560 }
    },
    A4: { 
      portrait: { width: 794, height: 1123 },
      landscape: { width: 1123, height: 794 }
    }
  };

  const handlePageSizeChange = (size) => {
    handleInputChange('flipbookSettings.pageSize', size);
    if (size && formData.flipbookSettings?.orientation) {
      applyDimensions(size, formData.flipbookSettings.orientation);
    }
  };

  const handleOrientationChange = (orientation) => {
    handleInputChange('flipbookSettings.orientation', orientation);
    if (formData.flipbookSettings?.pageSize) {
      applyDimensions(formData.flipbookSettings.pageSize, orientation);
    }
  };

  const applyDimensions = (size, orientation) => {
    const dims = PAGE_SIZES[size]?.[orientation];
    if (dims) {
      handleInputChange('flipbookSettings.minWidth', dims.width);
      handleInputChange('flipbookSettings.maxWidth', dims.width);
      handleInputChange('flipbookSettings.minHeight', dims.height);
      handleInputChange('flipbookSettings.maxHeight', dims.height);
    }
  };

  const handleFileUpload = async (file, fileType) => {
      if (!file) return;
  
      setUploadStates(prev => ({ ...prev, [fileType]: { uploading: true, error: null } }));
      
      try {
        const result = await firestoreService.uploadFile(file, `pb-${book.id}/flipbook/${fileType}`);
        
        setFormData(prev => ({
          ...prev,
          [fileType === 'logo' ? 'logoUrl' : 
          fileType === 'favicon' ? 'faviconUrl' : 
          fileType === 'headerBackground' ? 'headerBackgroundUrl' : 
          fileType]: result.url
        }));
  
        setUploadStates(prev => ({ ...prev, [fileType]: { uploading: false, error: null } }));
        
      } catch (error) {
        console.error(`Error uploading ${fileType}:`, error);
        setUploadStates(prev => ({ 
          ...prev, 
          [fileType]: { uploading: false, error: error.message } 
        }));
      }
    };

    const handleLogoSelect = (e) => {
      const file = e.target.files[0];
      if (file && file.type.startsWith('image/')) {
        setLogoFile(file);
        const reader = new FileReader();
        reader.onload = (e) => setLogoPreview(e.target.result);
        reader.readAsDataURL(file);
        setHasChanges(true);
      }
    };

    const handleCoverImageSelect = (e) => {
      const file = e.target.files[0];
      if (file && file.type.startsWith('image/')) {
        setCoverImageFile(file);
        const reader = new FileReader();
        reader.onload = (e) => setCoverImagePreview(e.target.result);
        reader.readAsDataURL(file);
        setHasChanges(true);
      }
    };

    const handleHeaderBackgroundSelect = (e) => {
      const file = e.target.files[0];
      if (file && file.type.startsWith('image/')) {
        setHeaderBackgroundFile(file);
        const reader = new FileReader();
        reader.onload = (e) => setHeaderBackgroundPreview(e.target.result);
        reader.readAsDataURL(file);
        setHasChanges(true);
      }
    };

    const handleFaviconSelect = (e) => {
      const file = e.target.files[0];
      if (file && file.type.startsWith('image/')) {
        setFaviconFile(file);
        const reader = new FileReader();
        reader.onload = (e) => setFaviconPreview(e.target.result);
        reader.readAsDataURL(file);
        setHasChanges(true);
      }
    };

    const handleOgImageSelect = (e) => {
      const file = e.target.files[0];
      if (file && file.type.startsWith('image/')) {
        setOgImageFile(file);
        const reader = new FileReader();
        reader.onload = (e) => setOgImagePreview(e.target.result);
        reader.readAsDataURL(file);
        setHasChanges(true);
      }
    };
      
  const uploadFiles = async () => {
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const uploads = [];
      const uploadedUrls = {};
      
      if (logoFile) {
        uploads.push({
          file: logoFile,
          path: 'logo',
          field: 'logoUrl'
        });
      }
      
      if (coverImageFile) {
        uploads.push({
          file: coverImageFile,
          path: 'cover',
          field: 'coverImageUrl'
        });
      }
      
      if (headerBackgroundFile) {
        uploads.push({
          file: headerBackgroundFile,
          path: 'header-bg',
          field: 'headerBackgroundUrl'
        });
      }

      if (ogImageFile) {
        uploads.push({
          file: ogImageFile,
          path: 'og-image',
          field: 'ogImage'
        });
      }

      if (faviconFile) {
        uploads.push({
          file: faviconFile,
          path: 'favicon',
          field: 'favicon'
        });
      }

      if (uploads.length === 0) return {};

      for (let i = 0; i < uploads.length; i++) {
        const upload = uploads[i];
        const result = await firestoreService.uploadFile(upload.file, `pb-${book.id}/flipbook/${upload.path}`);
        
        uploadedUrls[upload.field] = result.url;
        
        setFormData(prev => ({
          ...prev,
          [upload.field]: result.url
        }));
        
        setUploadProgress(Math.round(((i + 1) / uploads.length) * 100));
      }

      return uploadedUrls;
      
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSave = async () => {
    if (!book) return;
    
    try {
      setSaving(true);
      setErrors({});

      // Upload files first and get their URLs
      const uploadedUrls = await uploadFiles();



      const updatedBook = {
        ...book,
        botName: formData.botName,
        description: formData.description,
        botCategory: formData.botCategory,

        coverImageUrl: uploadedUrls.coverImageUrl || formData.coverImageUrl,
        logoUrl: uploadedUrls.logoUrl || formData.logoUrl,
        headerBackgroundUrl: uploadedUrls.headerBackgroundUrl || formData.headerBackgroundUrl,
        favicon: uploadedUrls.favicon || formData.favicon || book.favicon || '',
        ogImage: uploadedUrls.ogImage || formData.ogImage || book.ogImage || '',

        published: formData.published,
        accessLevel: formData.accessLevel,
        requireLogin: formData.requireLogin,
        allowIndexing: formData.allowIndexing,
        authorizedUsers: formData.authorizedUsers,
        allowPrint: formData.allowPrint,
        allowDownload: formData.allowDownload,
        enableRightClickProtection: formData.enableRightClickProtection,    
        
        styling: {
          primaryColor: formData.primaryColor,
          backgroundColor: formData.backgroundColor,
          headerColor: formData.headerColor,
          headerTextColor: formData.headerTextColor,
          headerOpacity: formData.headerOpacity,
          textColor: formData.textColor,
          accentColor: formData.accentColor,
          fontFamily: formData.fontFamily,
          headerRadius: formData.headerRadius,
          cardRadius: formData.cardRadius,
          shadowLevel: formData.shadowLevel,
          logoInvert: formData.logoInvert,
          gallerySidebarBg: formData.gallerySidebarBg,
          gallerySidebarShadow: formData.gallerySidebarShadow
        },
        
        headerSettings: formData.headerSettings, 
        
        flipbookSettings: {
          viewMode: formData.flipbookSettings.viewMode,
          enableSound: formData.flipbookSettings.enableSound,
          enableNarration: formData.flipbookSettings.enableNarration,
          autoPlay: formData.flipbookSettings.autoPlay,
          showPageNumbers: formData.flipbookSettings.showPageNumbers,
          flipDuration: formData.flipbookSettings.flipDuration,
          shadowOpacity: formData.flipbookSettings.shadowOpacity,
          pageSize: formData.flipbookSettings.pageSize,
          orientation: formData.flipbookSettings.orientation,
          minWidth: formData.flipbookSettings.minWidth,
          maxWidth: formData.flipbookSettings.maxWidth,
          minHeight: formData.flipbookSettings.minHeight,
          maxHeight: formData.flipbookSettings.maxHeight,
          allowedResponseTypes: formData.flipbookSettings.allowedResponseTypes,
          editors: formData.flipbookSettings.editors,
          enableResponseModal: formData.flipbookSettings.enableResponseModal,
          enableGradingSidebar: formData.flipbookSettings.enableGradingSidebar,
        },

        maxMediaSize: formData.maxMediaSize,
        autoGenerateThumbnails: formData.autoGenerateThumbnails,
        enableImageOptimization: formData.enableImageOptimization,

        seoTitle: formData.seoTitle,
        seoDescription: formData.seoDescription,
        seoKeywords: formData.seoKeywords,
        ogImage: uploadedUrls.ogImage || formData.ogImage,
        customDomain: formData.customDomain,
        customPath: formData.customPath,
        customCSS: formData.customCSS,
        favicon: uploadedUrls.favicon || formData.favicon,

        updatedAt: new Date()
      };

      await firestoreService.updateBook(book.id, updatedBook);
      onUpdate(updatedBook);       
      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error('Error saving flipbook settings:', error);
      setErrors({ general: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <BasicInfoTab
            formData={formData}
            onInputChange={handleInputChange}
            onFileUpload={handleFileUpload}
            errors={errors}
            bookId={bookId}
            templateType="flipbook"
          />
        );

      case 'design':
        return (
          <DesignBrandingSection
            formData={formData}
            onInputChange={handleInputChange}
            bookId={bookId}
          />
        );

      case 'header':
        return (
          <HeaderSettings
            formData={formData}
            onInputChange={handleInputChange}
             onImageUpload={handleFileUpload}
          />
        );

      case 'flipbook':
        return (
          <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Display Mode</h3>
                
                <div className="grid grid-cols-2 gap-4">
                <label className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.flipbookSettings?.viewMode === 'single' 
                    ? 'border-purple-600 bg-purple-50' 
                    : 'border-gray-200 hover:border-purple-300'
                }`}>
                    <input
                    type="radio"
                    name="viewMode"
                    value="single"
                    checked={formData.flipbookSettings?.viewMode === 'single'}
                    onChange={(e) => handleInputChange('flipbookSettings.viewMode', e.target.value)}
                    className="sr-only"
                    />
                    <BookOpen className="w-8 h-8 text-purple-600" />
                    <div className="text-center">
                    <div className="font-medium text-gray-900">Single Page</div>
                    <div className="text-sm text-gray-500">One page at a time</div>
                    </div>
                </label>

                <label className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.flipbookSettings?.viewMode === 'double' 
                    ? 'border-purple-600 bg-purple-50' 
                    : 'border-gray-200 hover:border-purple-300'
                }`}>
                    <input
                    type="radio"
                    name="viewMode"
                    value="double"
                    checked={formData.flipbookSettings?.viewMode === 'double'}
                    onChange={(e) => handleInputChange('flipbookSettings.viewMode', e.target.value)}
                    className="sr-only"
                    />
                    <BookOpen className="w-8 h-8 text-purple-600" />
                    <div className="text-center">
                    <div className="font-medium text-gray-900">Double Page</div>
                    <div className="text-sm text-gray-500">Two pages spread</div>
                    </div>
                </label>
                </div>
            </div>

            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Flipbook Behavior</h3>
              
              <div className="space-y-4">
                <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.flipbookSettings?.enableSound}
                    onChange={(e) => handleInputChange('flipbookSettings.enableSound', e.target.checked)}
                    className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">Enable Page Flip Sound</div>
                    <div className="text-sm text-gray-500">Play sound effect when turning pages</div>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.flipbookSettings?.enableNarration}
                    onChange={(e) => handleInputChange('flipbookSettings.enableNarration', e.target.checked)}
                    className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">Enable Page Narration</div>
                    <div className="text-sm text-gray-500">Allow voice narration for each page</div>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.flipbookSettings?.autoPlay}
                    onChange={(e) => handleInputChange('flipbookSettings.autoPlay', e.target.checked)}
                    className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">Auto-Play</div>
                    <div className="text-sm text-gray-500">Automatically start flipping pages</div>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.flipbookSettings?.showPageNumbers}
                    onChange={(e) => handleInputChange('flipbookSettings.showPageNumbers', e.target.checked)}
                    className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">Show Page Numbers</div>
                    <div className="text-sm text-gray-500">Display page numbers on flipbook</div>
                  </div>
                </label>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Animation Settings</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Flip Duration (ms)
                  </label>
                  <input
                    type="number"
                    min="500"
                    max="2000"
                    step="100"
                    value={formData.flipbookSettings?.flipDuration}
                    onChange={(e) => handleInputChange('flipbookSettings.flipDuration', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    How long the page flip animation takes (500-2000ms)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shadow Opacity
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={formData.flipbookSettings?.shadowOpacity}
                    onChange={(e) => handleInputChange('flipbookSettings.shadowOpacity', parseFloat(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>None</span>
                    <span>{formData.flipbookSettings?.shadowOpacity}</span>
                    <span>Full</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Interactive Features */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Interactive Features</h3>
              
              <div className="space-y-4">
                <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.flipbookSettings?.enableResponseModal ?? true}
                    onChange={(e) => handleInputChange('flipbookSettings.enableResponseModal', e.target.checked)}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <MessageSquarePlus className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-gray-900">Enable Response Modal</span>
                    </div>
                    <div className="text-sm text-gray-500">Allow users to submit responses on pages</div>
                  </div>
                </label>

                <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.flipbookSettings?.enableGradingSidebar ?? false}
                    onChange={(e) => handleInputChange('flipbookSettings.enableGradingSidebar', e.target.checked)}
                    className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Award className="w-5 h-5 text-purple-600" />
                      <span className="font-medium text-gray-900">Enable Grading Sidebar</span>
                    </div>
                    <div className="text-sm text-gray-500">Show grading sidebar for instructors/editors</div>
                  </div>
                </label>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Response Configuration</h3>
              
              <div className="space-y-6">
                {/* Allowed Response Types */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Allowed Response Types
                  </label>
                  <p className="text-sm text-gray-500 mb-3">
                    Select which types of responses users can submit
                  </p>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.flipbookSettings?.allowedResponseTypes?.includes('practice') ?? true}
                        onChange={(e) => {
                          const current = formData.flipbookSettings?.allowedResponseTypes || ['practice', 'graded', 'model'];
                          const updated = e.target.checked 
                            ? [...current, 'practice'].filter((v, i, a) => a.indexOf(v) === i)
                            : current.filter(t => t !== 'practice');
                          handleInputChange('flipbookSettings.allowedResponseTypes', updated);
                        }}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-600" />
                          <span className="font-medium text-gray-900">Practice Response</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">For practice and self-assessment</p>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.flipbookSettings?.allowedResponseTypes?.includes('graded') ?? true}
                        onChange={(e) => {
                          const current = formData.flipbookSettings?.allowedResponseTypes || ['practice', 'graded', 'model'];
                          const updated = e.target.checked 
                            ? [...current, 'graded'].filter((v, i, a) => a.indexOf(v) === i)
                            : current.filter(t => t !== 'graded');
                          handleInputChange('flipbookSettings.allowedResponseTypes', updated);
                        }}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <GraduationCap className="w-4 h-4 text-purple-600" />
                          <span className="font-medium text-gray-900">Graded Response</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Will be graded by instructor</p>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.flipbookSettings?.allowedResponseTypes?.includes('model') ?? true}
                        onChange={(e) => {
                          const current = formData.flipbookSettings?.allowedResponseTypes || ['practice', 'graded', 'model'];
                          const updated = e.target.checked 
                            ? [...current, 'model'].filter((v, i, a) => a.indexOf(v) === i)
                            : current.filter(t => t !== 'model');
                          handleInputChange('flipbookSettings.allowedResponseTypes', updated);
                        }}
                        className="w-4 h-4 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Award className="w-4 h-4 text-green-600" />
                          <span className="font-medium text-gray-900">Model Answer</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Reference solution (only owners/editors can add)</p>
                      </div>
                    </label>
                  </div>
                </div>



                {/* Editors Management */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Flipbook Editors
                  </label>
                  <p className="text-sm text-gray-500 mb-3">
                    Add user emails who can submit model answers and manage content
                  </p>
                  <div className="space-y-3">
                    {(formData.flipbookSettings?.editors || []).map((editor, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="email"
                          value={editor}
                          onChange={(e) => {
                            const editors = [...(formData.flipbookSettings?.editors || [])];
                            editors[index] = e.target.value;
                            handleInputChange('flipbookSettings.editors', editors);
                          }}
                          placeholder="editor@example.com"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const editors = (formData.flipbookSettings?.editors || []).filter((_, i) => i !== index);
                            handleInputChange('flipbookSettings.editors', editors);
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const editors = [...(formData.flipbookSettings?.editors || []), ''];
                        handleInputChange('flipbookSettings.editors', editors);
                      }}
                      className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-400 hover:text-purple-600 transition-colors"
                    >
                      + Add Editor
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Page Dimensions</h3>
              
              {/* Preset Selection */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page Size (Optional)
                  </label>
                  <div className="grid grid-cols-4 gap-3">
                    <button
                      type="button"
                      onClick={() => handlePageSizeChange('')}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        !formData.flipbookSettings?.pageSize
                          ? 'border-purple-600 bg-purple-50 text-purple-700'
                          : 'border-gray-300 text-gray-700 hover:border-purple-300'
                      }`}
                    >
                      Custom
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePageSizeChange('DL')}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        formData.flipbookSettings?.pageSize === 'DL'
                          ? 'border-purple-600 bg-purple-50 text-purple-700'
                          : 'border-gray-300 text-gray-700 hover:border-purple-300'
                      }`}
                    >
                      DL
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePageSizeChange('A5')}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        formData.flipbookSettings?.pageSize === 'A5'
                          ? 'border-purple-600 bg-purple-50 text-purple-700'
                          : 'border-gray-300 text-gray-700 hover:border-purple-300'
                      }`}
                    >
                      A5
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePageSizeChange('A4')}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        formData.flipbookSettings?.pageSize === 'A4'
                          ? 'border-purple-600 bg-purple-50 text-purple-700'
                          : 'border-gray-300 text-gray-700 hover:border-purple-300'
                      }`}
                    >
                      A4
                    </button>
                  </div>
                </div>

                {formData.flipbookSettings?.pageSize && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Orientation
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => handleOrientationChange('portrait')}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                          formData.flipbookSettings?.orientation === 'portrait'
                            ? 'border-purple-600 bg-purple-50 text-purple-700'
                            : 'border-gray-300 text-gray-700 hover:border-purple-300'
                        }`}
                      >
                        <Monitor className="w-5 h-5 rotate-0" />
                        <span>Portrait</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOrientationChange('landscape')}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                          formData.flipbookSettings?.orientation === 'landscape'
                            ? 'border-purple-600 bg-purple-50 text-purple-700'
                            : 'border-gray-300 text-gray-700 hover:border-purple-300'
                        }`}
                      >
                        <Monitor className="w-5 h-5 rotate-90" />
                        <span>Landscape</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Manual Dimension Controls */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-medium text-gray-900">Custom Dimensions</h4>
                  <Maximize2 className="w-4 h-4 text-gray-400" />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Min Width (px)
                    </label>
                    <input
                      type="number"
                      min="200"
                      value={formData.flipbookSettings?.minWidth}
                      onChange={(e) => handleInputChange('flipbookSettings.minWidth', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Width (px)
                    </label>
                    <input
                      type="number"
                      min="200"
                      value={formData.flipbookSettings?.maxWidth}
                      onChange={(e) => handleInputChange('flipbookSettings.maxWidth', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Min Height (px)
                    </label>
                    <input
                      type="number"
                      min="200"
                      value={formData.flipbookSettings?.minHeight}
                      onChange={(e) => handleInputChange('flipbookSettings.minHeight', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Height (px)
                    </label>
                    <input
                      type="number"
                      min="200"
                      value={formData.flipbookSettings?.maxHeight}
                      onChange={(e) => handleInputChange('flipbookSettings.maxHeight', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                {formData.flipbookSettings?.pageSize && formData.flipbookSettings?.orientation && (
                  <p className="text-xs text-gray-500 mt-3">
                    Current preset: {formData.flipbookSettings.pageSize} {formData.flipbookSettings.orientation} 
                    ({PAGE_SIZES[formData.flipbookSettings.pageSize][formData.flipbookSettings.orientation].width}x
                    {PAGE_SIZES[formData.flipbookSettings.pageSize][formData.flipbookSettings.orientation].height}px)
                  </p>
                )}
              </div>
            </div>
          </div>
        );

        case 'media':
          return (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Images & Media</h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                          {/* Logo Upload */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                              Logo
                            </label>
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                              {logoPreview ? (
                                <div className="relative">
                                  <img
                                    src={logoPreview}
                                    alt="Logo Preview"
                                    className="w-full h-24 object-contain rounded-lg bg-gray-50"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setLogoPreview('');
                                      setLogoFile(null);
                                      handleInputChange('logoUrl', '');
                                    }}
                                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <div>
                                  <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                                  <div className="mt-2">
                                    <label htmlFor="logo-upload" className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                                      <Upload className="h-4 w-4 inline mr-2" />
                                      Upload Logo
                                    </label>
                                    <input
                                      id="logo-upload"
                                      type="file"
                                      accept="image/*"
                                      onChange={handleLogoSelect}
                                      className="hidden"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
      
                          {/* Cover Image Upload */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                              Cover Image
                            </label>
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                              {coverImagePreview ? (
                                <div className="relative">
                                  <img
                                    src={coverImagePreview}
                                    alt="Cover Preview"
                                    className="w-full h-24 object-cover rounded-lg"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setCoverImagePreview('');
                                      setCoverImageFile(null);
                                      handleInputChange('coverImageUrl', '');
                                    }}
                                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <div>
                                  <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                                  <div className="mt-2">
                                    <label htmlFor="cover-upload" className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                                      <Upload className="h-4 w-4 inline mr-2" />
                                      Upload Cover
                                    </label>
                                    <input
                                      id="cover-upload"
                                      type="file"
                                      accept="image/*"
                                      onChange={handleCoverImageSelect}
                                      className="hidden"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
      
                          {/* Header Background Upload */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                              Header Background
                            </label>
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                              {headerBackgroundPreview ? (
                                <div className="relative">
                                  <img
                                    src={headerBackgroundPreview}
                                    alt="Header Background Preview"
                                    className="w-full h-24 object-cover rounded-lg"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setHeaderBackgroundPreview('');
                                      setHeaderBackgroundFile(null);
                                      handleInputChange('headerBackgroundUrl', '');
                                    }}
                                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <div>
                                  <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                                  <div className="mt-2">
                                    <label htmlFor="header-bg-upload" className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                                      <Upload className="h-4 w-4 inline mr-2" />
                                      Upload Background
                                    </label>
                                    <input
                                      id="header-bg-upload"
                                      type="file"
                                      accept="image/*"
                                      onChange={handleHeaderBackgroundSelect}
                                      className="hidden"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
      
                      {/* Media Settings */}
                      <div>
                        <h4 className="text-md font-semibold text-gray-900 mb-4">Media Upload Settings</h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Maximum File Size (MB)
                            </label>
                            <select
                              value={formData.maxMediaSize}
                              onChange={(e) => handleInputChange('maxMediaSize', parseInt(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value={5}>5 MB</option>
                              <option value={10}>10 MB</option>
                              <option value={25}>25 MB</option>
                              <option value={50}>50 MB</option>
                              <option value={100}>100 MB</option>
                            </select>
                          </div>
      
                          <div className="space-y-3">
                            {[
                              { key: 'autoGenerateThumbnails', label: 'Auto-generate Thumbnails', desc: 'Create thumbnails for videos and large images' },
                              { key: 'enableImageOptimization', label: 'Image Optimization', desc: 'Compress images for faster loading' }
                            ].map(({ key, label, desc }) => (
                              <label key={key} className="flex items-start gap-3">
                                <input
                                  type="checkbox"
                                  checked={formData[key]}
                                  onChange={(e) => handleInputChange(key, e.target.checked)}
                                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div>
                                  <div className="font-medium text-gray-900">{label}</div>
                                  <div className="text-sm text-gray-500">{desc}</div>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );

      case 'privacy':
        return (
          <PrivacySection
            formData={formData}
            onInputChange={handleInputChange}
            templateType="organisation"
          />
        );

      case 'seo':
        return (
          <SEOSection
            formData={formData}
            onInputChange={handleInputChange}
            onImageUpload={(file, fileType) => {
              // Route to appropriate handler based on fileType
              if (fileType === 'favicon') {
                const event = { target: { files: [file] } };
                handleFaviconSelect(event);
              } else if (fileType === 'ogImage') {
                const event = { target: { files: [file] } };
                handleOgImageSelect(event);
              }
            }}
            uploadStates={uploadStates}
            errors={errors}
            bookId={bookId}
            faviconPreview={faviconPreview}
            ogImagePreview={ogImagePreview}
            onRemoveFavicon={() => {
              setFaviconFile(null);
              setFaviconPreview('');
              handleInputChange('favicon', '');
            }}
            onRemoveOgImage={() => {
              setOgImageFile(null);
              setOgImagePreview('');
              handleInputChange('ogImage', '');
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <SettingsModal
      isOpen={true}
      onClose={onClose}
      title="Flipbook Settings"
      subtitle="Configure your interactive flipbook"
      onSave={handleSave}
      saving={saving}
      hasChanges={hasChanges}
    >
      <div className="flex h-full overflow-hidden">
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>
        <div className="flex-1 overflow-y-auto min-h-0">
         <div className="p-6">
          {renderTabContent()}
        </div>
      </div>
    </SettingsModal>
  );
};